/**
 * Thin typed web API client for the BCT (Backchannel Tree) feature (web/v2).
 *
 * Same conventions as `imports.client.ts`:
 * - `makeAuthedFetch` by default (single-flight 401 -> POST /auth/refresh ->
 *   retry-once), NOT bare `globalThis.fetch` — see `imports.client.ts`'s own
 *   "Bug fixed 2026-07-05" docstring. `handleSave`'s poll loop
 *   (`BctPage.tsx`) can run for up to 2 minutes; without this, a 401 hit
 *   anywhere in that window (the 1h access-token cookie expiring mid-poll)
 *   throws a raw, unrefreshed `BctApiError` instead of transparently
 *   refreshing and retrying — and leaves the session in the same
 *   un-refreshed state the very next `imports.client.ts` call (navigating
 *   back to the imports list after Save) can inherit, surfacing as
 *   "Couldn't load imports. Please try again." even though that client
 *   itself is fixed. Root-caused 2026-07-16 from a live repro.
 * - `credentials: "include"` for cookie auth
 * - Typed end-to-end; types mirrored from the API schemas until OpenAPI
 *   codegen is re-run (will be replaced by `@nu-assist/contracts` imports)
 */

import type { components } from "@nu-assist/contracts";
import { makeAuthedFetch } from "@nu-assist/shared-fe";
import { clearStoredSelectedImport } from "../lib/selectedImportStorage";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

/** ── Contract types (mirrors bct/schemas.py) ── */

export interface BctCompany {
  name: string;
  id?: string | null;
  start_year?: number | null;
  end_year?: number | null;
}

/**
 * How a run's in-scope titles were sorted into role categories — the contract
 * of Jared's Sheets add-on. `categories` is the display order; `mapping` sends
 * every in-scope title to one of them, or to `null` when it is not one of the
 * roles to track (those people are left out of the map).
 */
export interface BctClassification {
  categories: string[];
  mapping: Record<string, string | null>;
}

/**
 * Response-side company shape. `Required<BctCompany>` removes the `?` (undefined/absent)
 * from every key — every field is always present in the JSON — but does NOT narrow
 * `| null` to non-null: `id: string | null`, `start_year: number | null`, etc. remain
 * nullable because the API legitimately returns null for optional fields.
 *
 * Use this for BctRunResponse/BctPreviewResponse; use BctCompany for request building.
 */
export type BctCompanyResponse = Required<BctCompany>;

export interface BctRunRequest {
  companies: BctCompany[];
  /** One "Roles to track" text for the whole run. Blank → the API applies
   *  the add-on's default description. */
  roles_description?: string | null;
  /** Save only: the preview's `preview_classification_id`, so the saved run
   *  keeps the answer the recruiter saw. The classification itself stays on
   *  the server; the WAF caps this body at ~8 KB. */
  preview_classification_id?: string | null;
}

export interface BctRunResponse {
  id: string;
  import_id: string;
  child_import_id: string | null;
  status: "pending" | "complete" | "failed";
  companies: BctCompanyResponse[];
  /** Null on runs saved before AI role categories (substring role filter). */
  roles_description?: string | null;
  /** Null until the run is classified, and on runs saved before AI role categories. */
  categories?: string[] | null;
  match_count: number | null;
  error: string | null;
  created_at: string;
  completed_at: string | null;
}

/**
 * A Company Map result row.
 *
 * **This is a hand-maintained mirror, not generated.** The preview response
 * declares `rows: list[dict[str, str]]` in `bct/schemas.py`, so the OpenAPI
 * contract carries no per-key shape and codegen cannot derive this type — the
 * keys live in `BCT_CSV_FIELDNAMES` (`api/app/web/v2/bct/domain.py`) and here,
 * and nothing enforces that the two agree. Change one, change the other.
 */
export interface BctResultRow {
  "First Name": string;
  "Last Name": string;
  Title: string;
  Company: string;
  Start: string;
  End: string;
  Degree: string;
  /** The role category Claude placed this person in. Blank only on the
   *  results of a run saved before AI role categories. */
  Category: string;
  /** Profile URL, so the preview can link the name the way the saved-import
   *  table already does. Empty when the contact has none — render plain text
   *  rather than a dead link. */
  "LinkedIn URL": string;
}

/** Response from the preview endpoint — results inline; only the
 *  classification is kept on the server (7 days), for the save to reference. */
export interface BctPreviewResponse {
  match_count: number;
  companies: BctCompanyResponse[];
  /** The roles-to-track text the titles were classified against (default applied). */
  roles_description: string;
  /** Optional in the type only because a web deploy can land before the API's;
   *  every current API returns it. */
  classification?: BctClassification;
  /** Send on save. Absent from an API older than this web. */
  preview_classification_id?: string;
  rows: BctResultRow[];
}

/** One known (company, LinkedIn ID) pair for the company typeahead — mirrors
 * `CompanySuggestion` in bct/schemas.py. Sourced from the parsed positions of
 * a single import's contacts (scoped by `import_id`). */
export interface CompanySuggestion {
  name: string;
  id: string;
  count: number;
}

interface CompanySuggestionsResponse {
  items: CompanySuggestion[];
}

/** `GET …/prompt` — the model calls behind a preview or a saved run, rebuilt
 *  server-side (the Company Map prompt viewer). Generated from the API. */
export type BctPromptResponse = components["schemas"]["BctPromptResponse"];
export type BctPromptStep = components["schemas"]["BctPromptStep"];
export type BctPromptCall = components["schemas"]["BctPromptCall"];

/** ── Errors ── */
export class BctApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    message?: string,
  ) {
    super(message ?? `Company Map API request failed (${status}) for ${url}`);
    this.name = "BctApiError";
    Object.setPrototypeOf(this, BctApiError.prototype);
  }
}

/** ── Client factory (same pattern as createImportsClient) ── */
export type FetchLike = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export interface BctClientConfig {
  apiBase?: string;
  fetchImpl?: FetchLike;
}

/** RFC 4180-compliant CSV line parser — handles quoted fields containing commas. */
function parseCsvLine(line: string): string[] {
  const fields: string[] = [];
  let i = 0;
  while (i < line.length) {
    if (line[i] === '"') {
      // Quoted field: consume until closing unescaped double-quote.
      let field = "";
      i++; // skip opening quote
      while (i < line.length) {
        if (line[i] === '"' && line[i + 1] === '"') {
          field += '"'; // escaped quote inside field
          i += 2;
        } else if (line[i] === '"') {
          i++; // skip closing quote
          break;
        } else {
          field += line[i++];
        }
      }
      fields.push(field);
      if (line[i] === ",") i++; // skip inter-field comma
    } else {
      // Unquoted field: consume until next comma or end of line.
      const commaIdx = line.indexOf(",", i);
      if (commaIdx === -1) {
        fields.push(line.slice(i));
        break;
      }
      fields.push(line.slice(i, commaIdx));
      i = commaIdx + 1;
    }
  }
  return fields;
}

function parseSimpleCsv(text: string): BctResultRow[] {
  // Normalise CRLF — Python's csv module writes \r\n by default.
  const lines = text.trim().replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (lines.length < 2) return [];
  const headers = parseCsvLine(lines[0]).map((h) => h.trim());
  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    return Object.fromEntries(
      headers.map((h, i) => [h, (values[i] ?? "").trim()]),
    ) as unknown as BctResultRow;
  });
}

export function createBctClient(cfg: BctClientConfig = {}) {
  const base = cfg.apiBase ?? API_BASE;
  // Default to the session-enforcing `makeAuthedFetch`, matching
  // `createImportsClient` — see this file's top docstring.
  const authedFetch: FetchLike =
    cfg.fetchImpl ??
    makeAuthedFetch({
      apiBase: base,
      onSessionExpired: () => {
        // The shared import selection is persisted and carries another
        // recruiter's data; a dead refresh token must not leave it for the
        // next person on this machine. See `clearStoredSelectedImport`.
        clearStoredSelectedImport();
        window.location.assign("/login");
      },
    });
  const doFetch: FetchLike = (input, init) => authedFetch(input, init);

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${base}${path}`;
    const res = await doFetch(url, {
      method,
      credentials: "include",
      headers: body !== undefined ? { "content-type": "application/json" } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      let detail = "";
      try {
        const json = await res.json();
        detail = json?.detail ?? "";
      } catch {
        // ignore parse failure
      }
      throw new BctApiError(res.status, url, detail || undefined);
    }
    return res.json() as Promise<T>;
  }

  return {
    /** POST /api/web/v2/imports/{importId}/bct-runs/preview — no DB writes */
    async previewBctRun(
      importId: string,
      body: BctRunRequest,
    ): Promise<BctPreviewResponse> {
      const raw = await request<
        Omit<BctPreviewResponse, "rows"> & { rows: Record<string, string>[] }
      >(
        "POST",
        `/api/web/v2/imports/${importId}/bct-runs/preview`,
        body,
      );
      return {
        ...raw,
        rows: raw.rows as unknown as BctResultRow[],
      };
    },

    /** POST /api/web/v2/imports/{importId}/bct-runs — saves to DB */
    async createBctRun(
      importId: string,
      body: BctRunRequest,
    ): Promise<BctRunResponse> {
      return request<BctRunResponse>(
        "POST",
        `/api/web/v2/imports/${importId}/bct-runs`,
        body,
      );
    },

    /** GET /api/web/v2/bct-runs/{runId} */
    async getBctRun(runId: string): Promise<BctRunResponse> {
      return request<BctRunResponse>("GET", `/api/web/v2/bct-runs/${runId}`);
    },

    /**
     * GET /api/web/v2/bct/companies?import_id=&q=&limit= — typeahead over known
     * companies parsed from the given import's positions. `import_id` is
     * required by the server (422 if missing). `q` matches on either the
     * company name or the LinkedIn ID; an empty `q` browses the import's top
     * companies (no minimum-length gate anymore). `limit` is optional (server
     * default 20; the typeahead passes 10 to cap the browse list).
     */
    async searchCompanies(
      importId: string,
      q: string,
      limit?: number,
    ): Promise<CompanySuggestion[]> {
      const params = new URLSearchParams({ import_id: importId, q: q.trim() });
      if (limit !== undefined) params.set("limit", String(limit));
      const res = await request<CompanySuggestionsResponse>(
        "GET",
        `/api/web/v2/bct/companies?${params.toString()}`,
      );
      return res.items;
    },

    /** GET /api/web/v2/imports/{importId}/bct-run — returns the BCT run that produced this import */
    async getBctRunForImport(importId: string): Promise<BctRunResponse> {
      return request<BctRunResponse>("GET", `/api/web/v2/imports/${importId}/bct-run`);
    },

    /** GET /api/web/v2/bct-runs/{runId}/prompt — the saved run's model calls,
     *  rebuilt. Every "cannot show it" is a 200 with a step `status`; 403 when
     *  the prompt viewer is limited to admins, 404 for an unknown run. */
    async getRunPrompt(runId: string): Promise<BctPromptResponse> {
      return request<BctPromptResponse>(
        "GET",
        `/api/web/v2/bct-runs/${encodeURIComponent(runId)}/prompt`,
      );
    },

    /** GET /api/web/v2/bct-previews/{previewId}/prompt — same, for a preview
     *  (its `preview_classification_id`, kept 7 days). */
    async getPreviewPrompt(previewId: string): Promise<BctPromptResponse> {
      return request<BctPromptResponse>(
        "GET",
        `/api/web/v2/bct-previews/${encodeURIComponent(previewId)}/prompt`,
      );
    },

    /** Returns the URL for the CSV download (use as anchor href or fetch). */
    getBctResultsCsvUrl(runId: string): string {
      return `${base}/api/web/v2/bct-runs/${runId}/results.csv`;
    },

    /** Fetch CSV and parse rows (for display in the results table) */
    async fetchBctResultRows(runId: string): Promise<BctResultRow[]> {
      const url = `${base}/api/web/v2/bct-runs/${runId}/results.csv`;
      const res = await doFetch(url, { credentials: "include" });
      if (!res.ok) throw new BctApiError(res.status, url);
      const text = await res.text();
      return parseSimpleCsv(text);
    },
  };
}

export type BctClient = ReturnType<typeof createBctClient>;
